// ============================================
// COMPONENT: FaultUpdateModal
// ============================================
// Archivo: src/components/modals/FaultUpdateModal.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from '../common';
import { CheckCircle } from 'lucide-react';
import { formatCLP, formatCLPInput, parseCLP } from '../../utils/formatters';
import { formatTipoFalla, formatEstadoFalla } from '../../utils/faultFormatters';

const isImage = (path) => /\.(jpe?g|png|gif|webp|bmp)$/i.test(path || '');

const FilePreview = ({ path, label }) => {
  const [url, setUrl] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (url) { setExpanded(e => !e); return; }
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/uploads/${path}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error();
      const { url: signed } = await res.json();
      setUrl(signed);
      if (isImage(path)) setExpanded(true);
      else window.open(signed, '_blank');
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [path, url]);

  if (error) return <p className="text-xs text-slate-400">⚠️ Archivo no disponible</p>;

  return (
    <div>
      <button
        type="button"
        onClick={load}
        disabled={loading}
        className="text-xs text-blue-400 hover:text-blue-300 hover:underline font-semibold disabled:opacity-50"
      >
        {loading ? 'Cargando…' : isImage(path) ? `🖼️ ${label}` : `📄 ${label}`}
      </button>
      {expanded && url && isImage(path) && (
        <div className="mt-2">
          <img
            src={url}
            alt={label}
            className="max-w-full max-h-64 rounded border border-slate-500 cursor-pointer object-contain"
            onClick={() => window.open(url, '_blank')}
            title="Clic para abrir en pantalla completa"
          />
          <p className="text-xs text-slate-400 mt-1">Clic en la imagen para ampliar</p>
        </div>
      )}
    </div>
  );
};

const FaultUpdateModal = ({ 
  show, 
  fault = null,
  onClose, 
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    estado: '',
    estadoEquipo: '',
    fechaSolucion: '',
    horaFin: '',
    solucion: '',
    responsableSolucion: '',
    estadoOc: '',
    numeroOc: '',
    fechaOc: '',
    proveedor: '',
    quienApruebaOc: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [newFiles, setNewFiles] = useState({ evidencia: null, oc: null, presupuesto: null });
  const [costos, setCostos] = useState({
    reparacion: 0,
    repuestos: 0,
    manoObra: 0
  });
  const [montoOc, setMontoOc] = useState(0);

  useEffect(() => {
    setNewFiles({ evidencia: null, oc: null, presupuesto: null });
    if (fault) {
      const today = new Date().toLocaleDateString('sv-SE');  // fecha local, no UTC
      const now = (() => { const d = new Date(); const p = n => String(n).padStart(2,'0'); return `${p(d.getHours())}:${p(d.getMinutes())}`; })();

      // Si ya tiene hora_fin guardada, mostrarla; si no, pre-llenar con hora local actual
      const existingFechaFin = fault.hora_fin
        ? fault.hora_fin.split('T')[0]
        : today;
      const existingHoraFin = fault.hora_fin
        ? fault.hora_fin.substring(11, 16)
        : now;

      setFormData({
        estado: fault.estado || 'reportada',
        estadoEquipo: fault.estado === 'resuelta' ? 'operativo' : 'en_falla',
        fechaSolucion: existingFechaFin,
        horaFin: existingHoraFin,
        solucion: fault.solucion || '',
        responsableSolucion: fault.responsable_solucion || '',
        estadoOc: fault.estado_oc || 'pendiente',
        numeroOc: fault.numero_oc || '',
        fechaOc: fault.fecha_oc || '',
        montoOc: fault.monto_oc || '',
        proveedor: fault.proveedor || '',
        quienApruebaOc: fault.quien_aprueba_oc || ''
      });
       
      setCostos({
      reparacion: parseFloat(fault.costo_reparacion) || 0,
      repuestos: parseFloat(fault.costo_repuestos) || 0,
      manoObra: parseFloat(fault.costo_mano_obra) || 0
    });
      setMontoOc(parseFloat(fault.monto_oc) || 0);
    }
  }, [fault, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-actualizar estado del equipo cuando la falla se resuelve
    if (name === 'estado' && value === 'resuelta') {
      setFormData(prev => ({ ...prev, estadoEquipo: 'operativo' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Si es primera resolución y horaFin está vacío (ej: campo borrado por usuario), usar hora actual
      const now = new Date();
      const pad = n => String(n).padStart(2, '0');
      const resolvedFormData = (formData.estado === 'resuelta' && !fault?.hora_fin && !formData.horaFin)
        ? { ...formData, horaFin: `${pad(now.getHours())}:${pad(now.getMinutes())}` }
        : formData;

      // Si la falla ya tenía hora_fin, no enviar horaFin/fechaSolucion — el backend conserva los originales
      const { horaFin: _h, fechaSolucion: _f, ...restFormData } = resolvedFormData;
      const dataToSend = {
        ...(fault?.hora_fin ? restFormData : resolvedFormData),  // primera resolución: enviar horaFin; ya resuelta: no
        costo_reparacion: costos.reparacion,
        costo_repuestos: costos.repuestos,
        costo_mano_obra: costos.manoObra,
        costo_total: costos.reparacion + costos.repuestos + costos.manoObra,
        monto_oc: montoOc
      };

      
      if (newFiles.evidencia)   dataToSend.fileEvidencia   = newFiles.evidencia;
      if (newFiles.oc)          dataToSend.fileOc          = newFiles.oc;
      if (newFiles.presupuesto) dataToSend.filePresupuesto = newFiles.presupuesto;
      await onSubmit(dataToSend, fault.id);
      onClose();
    } catch (error) {
      console.error('Error:', error);
      setErrors({ general: error.message || 'Error al actualizar falla' });
    } finally {
      setLoading(false);
    }
  };

  const estadoFallaOptions = [
    { value: 'reportada', label: 'Reportada' },
    { value: 'en_revision', label: 'En Revisión' },
    { value: 'esperando_repuesto', label: 'Esperando Repuesto' },
    { value: 'resuelta', label: 'Resuelta' }
  ];

  const estadoEquipoOptions = [
    { value: 'operativo', label: 'Operativo' },
    { value: 'en_falla', label: 'En Falla' },
    { value: 'deshabilitado', label: 'Deshabilitado' }
  ];

  const estadoOcOptions = [
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'aprobada', label: 'Aprobada' },
    { value: 'rechazada', label: 'Rechazada' },
    { value: 'en_proceso', label: 'En Proceso' },
    { value: 'completada', label: 'Completada' }
  ];

  if (!fault) return null;

  // Calcular tiempo transcurrido
  const tiempoTranscurrido = () => {
    if (!fault.hora_inicio) return 'N/A';
    const inicio = new Date(fault.hora_inicio);
    const ahora = new Date();
    const diff = Math.floor((ahora - inicio) / (1000 * 60)); // minutos
    
    // Si el tiempo es negativo (fecha futura), mostrar "En curso"
    if (diff < 0) return 'En curso';
    if (diff < 1) return 'Recién reportada';
    if (diff < 60) return `${diff} min`;
    
    const horas = Math.floor(diff / 60);
    const minutos = diff % 60;
    return `${horas}h ${minutos}m`;
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      title="Actualizar Estado de Falla"
      maxWidth="3xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Info de la falla */}
        <div className="bg-slate-700 p-4 rounded-lg">
          <h3 className="font-semibold text-white mb-2">
            {formatTipoFalla(fault.tipo_falla)}
          </h3>
          <p className="text-slate-300 text-sm mb-3">{fault.descripcion}</p>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-400">Equipo:</span>
              <p className="text-white">{fault.equipment_name || 'General de planta'}</p>
            </div>
            <div>
              <span className="text-slate-400">Reportado:</span>
              <p className="text-white">
                {new Date(fault.fecha_reporte).toLocaleDateString('es-CL')}
              </p>
            </div>
            <div>
              <span className="text-slate-400">Tiempo transcurrido:</span>
              <p className="text-white font-semibold">{tiempoTranscurrido()}</p>
            </div>
            <div>
              <span className="text-slate-400">Estado actual:</span>
              <p className="text-white">{formatEstadoFalla(fault.estado)}</p>
            </div>
          </div>
        </div>

        {/* Error general */}
        {errors.general && (
          <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-2 rounded">
            {errors.general}
          </div>
        )}

        {/* Estados */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Estado de la Falla <span className="text-red-400">*</span>
            </label>
            <select
              name="estado"
              value={formData.estado}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
            >
              {estadoFallaOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Estado del Equipo <span className="text-red-400">*</span>
            </label>
            <select
              name="estadoEquipo"
              value={formData.estadoEquipo}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
            >
              {estadoEquipoOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Si está resuelta, mostrar campos de solución */}
        {formData.estado === 'resuelta' && (
          <>
            <div className="bg-green-500/10 border border-green-500 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="text-green-500" size={20} />
                <h3 className="text-white font-semibold">Datos de Solución</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Fecha Solución
                  </label>
                  {fault?.hora_fin ? (
                    <div className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 text-sm">
                      {formData.fechaSolucion} <span className="text-xs text-slate-500">(automático)</span>
                    </div>
                  ) : (
                    <input
                      type="date"
                      name="fechaSolucion"
                      value={formData.fechaSolucion}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Hora Fin
                  </label>
                  {fault?.hora_fin ? (
                    <div className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 text-sm">
                      {formData.horaFin} <span className="text-xs text-slate-500">(automático)</span>
                    </div>
                  ) : (
                    <input
                      type="time"
                      name="horaFin"
                      value={formData.horaFin}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Descripción de la Solución
                </label>
                <textarea
                  name="solucion"
                  value={formData.solucion}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Describa cómo se resolvió la falla..."
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Responsable de la Solución
                </label>
                <input
                  type="text"
                  name="responsableSolucion"
                  value={formData.responsableSolucion}
                  onChange={handleChange}
                  placeholder="Nombre del técnico"
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
                />
              </div>
            </div>

            {/* Costos */}
            <div className="bg-slate-700 p-4 rounded-lg">
              <h3 className="text-white font-semibold mb-3">Costos (CLP)</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Reparación
                  </label>
                  <input
                  type="text"
                  value={formatCLPInput(costos.reparacion.toString())}
                  onChange={(e) => {
                    const formatted = formatCLPInput(e.target.value);
                    const numeric = parseCLP(formatted);
                    setCostos({ ...costos, reparacion: numeric });
                  }}
                  placeholder="0"
                  className="w-full px-4 py-3 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400"
                />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Repuestos
                  </label>
                  <input
                  type="text"
                  value={formatCLPInput(costos.repuestos.toString())}
                  onChange={(e) => {
                    const formatted = formatCLPInput(e.target.value);
                    const numeric = parseCLP(formatted);
                    setCostos({ ...costos, repuestos: numeric });
                  }}
                  placeholder="0"
                  className="w-full px-4 py-3 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400"
                />

                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Mano de Obra
                  </label>
                  <input
                  type="text"
                  value={formatCLPInput(costos.manoObra.toString())}
                  onChange={(e) => {
                    const formatted = formatCLPInput(e.target.value);
                    const numeric = parseCLP(formatted);
                    setCostos({ ...costos, manoObra: numeric });
                  }}
                  placeholder="0"
                  className="w-full px-4 py-3 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400"
                />
                </div>
              </div>
              <div className="mt-3 text-right">
                <span className="text-slate-400">Total: </span>
                <span className="text-white font-bold text-lg">
                  {formatCLP(costos.reparacion + costos.repuestos + costos.manoObra)}
                </span>
              </div>
            </div>
          </>
        )}

        {/* OC */}
        <div className="bg-slate-700 p-4 rounded-lg">
          <h3 className="text-white font-semibold mb-3">Orden de Compra</h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Número de OC
              </label>
              <input
                type="text"
                name="numeroOc"
                value={formData.numeroOc}
                onChange={handleChange}
                placeholder="Ej: OC-2024-001"
                className="w-full px-4 py-3 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Estado OC
              </label>
              <select
                name="estadoOc"
                value={formData.estadoOc}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-600 border border-slate-500 rounded-lg text-white"
              >
                {estadoOcOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Fecha OC
              </label>
              <input
                type="date"
                name="fechaOc"
                value={formData.fechaOc}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-600 border border-slate-500 rounded-lg text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Monto OC (CLP)
              </label>
              <input
                type="text"  // ✅
                value={formatCLPInput(montoOc.toString())}
                onChange={(e) => {
                  const formatted = formatCLPInput(e.target.value);
                  const numeric = parseCLP(formatted);
                  setMontoOc(numeric);
                }}
              placeholder="0"
              className="w-full px-4 py-3 bg-slate-600 border border-slate-500 rounded-lg text-white"
            />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Proveedor
              </label>
              <input
                type="text"
                name="proveedor"
                value={formData.proveedor}
                onChange={handleChange}
                placeholder="Nombre del proveedor"
                className="w-full px-4 py-3 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Quien Aprueba
              </label>
              <input
                type="text"
                name="quienApruebaOc"
                value={formData.quienApruebaOc}
                onChange={handleChange}
                placeholder="Nombre del aprobador"
                className="w-full px-4 py-3 bg-slate-600 border border-slate-500 rounded-lg text-white placeholder-slate-400"
              />
            </div>
          </div>
        </div>

        {/* Archivos adjuntos */}
        <div className="bg-slate-700 rounded-lg p-4 space-y-4">
          <h3 className="text-white font-semibold text-sm">Archivos Adjuntos</h3>

          {/* Evidencia */}
          <div>
            <p className="text-slate-300 text-xs font-semibold mb-1">Evidencia</p>
            {fault.archivo_evidencia_path && !newFiles.evidencia && (
              <FilePreview path={fault.archivo_evidencia_path} label="Ver archivo actual" />
            )}
            <label className="mt-2 flex items-center gap-2 cursor-pointer text-xs text-blue-400 hover:text-blue-300">
              📎 {newFiles.evidencia ? newFiles.evidencia.name : 'Subir nueva evidencia'}
              <input type="file" accept="image/*,.pdf" className="hidden"
                onChange={e => setNewFiles(p => ({ ...p, evidencia: e.target.files[0] || null }))} />
            </label>
          </div>

          {/* OC */}
          <div>
            <p className="text-slate-300 text-xs font-semibold mb-1">Orden de Compra</p>
            {fault.archivo_oc_path && !newFiles.oc && (
              <FilePreview path={fault.archivo_oc_path} label="Ver archivo actual" />
            )}
            <label className="mt-2 flex items-center gap-2 cursor-pointer text-xs text-blue-400 hover:text-blue-300">
              📎 {newFiles.oc ? newFiles.oc.name : 'Subir nueva OC'}
              <input type="file" accept="image/*,.pdf" className="hidden"
                onChange={e => setNewFiles(p => ({ ...p, oc: e.target.files[0] || null }))} />
            </label>
          </div>

          {/* Presupuesto */}
          <div>
            <p className="text-slate-300 text-xs font-semibold mb-1">Presupuesto</p>
            {fault.archivo_presupuesto_path && !newFiles.presupuesto && (
              <FilePreview path={fault.archivo_presupuesto_path} label="Ver archivo actual" />
            )}
            <label className="mt-2 flex items-center gap-2 cursor-pointer text-xs text-blue-400 hover:text-blue-300">
              📎 {newFiles.presupuesto ? newFiles.presupuesto.name : 'Subir nuevo presupuesto'}
              <input type="file" accept="image/*,.pdf" className="hidden"
                onChange={e => setNewFiles(p => ({ ...p, presupuesto: e.target.files[0] || null }))} />
            </label>
          </div>
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-600">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? 'Actualizando...' : 'Actualizar Estado'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default FaultUpdateModal;
